/**
 * MessageTemplatesPage - Страница управления шаблонами сообщений
 *
 * Доступ: Admin, Manager
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Title,
  Button,
  Group,
  Table,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Loader,
  Center,
  Box,
  Tooltip,
  Stack,
} from '@mantine/core';
import {
  IconPlus,
  IconDots,
  IconEye,
  IconPencil,
  IconTrash,
  IconTemplate,
  IconBrandWhatsapp,
  IconBrandTelegram,
  IconBrandVk,
  IconPhone,
  IconMail,
} from '@tabler/icons-react';
import MessageTemplateFormModal from '../components/MessageTemplates/MessageTemplateFormModal';
import MessageTemplateViewModal from '../components/MessageTemplates/MessageTemplateViewModal';
import tokens from '../theme/designTokens';

/**
 * Иконки для каналов
 */
const CHANNEL_ICONS = {
  whatsapp: { icon: IconBrandWhatsapp, color: 'green', label: 'WhatsApp' },
  telegram: { icon: IconBrandTelegram, color: 'blue', label: 'Telegram' },
  viber: { icon: IconPhone, color: 'grape', label: 'Viber' },
  vk: { icon: IconBrandVk, color: 'indigo', label: 'VK' },
  email: { icon: IconMail, color: 'cyan', label: 'Email' },
};

/**
 * API функции для шаблонов
 */
const templatesApi = {
  async getTemplates() {
    const response = await fetch('/api/message-templates', {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить шаблоны');
    }
    return response.json();
  },

  async deleteTemplate(id) {
    const response = await fetch(`/api/message-templates/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить шаблон');
    }
  },
};

/**
 * Форматирование даты
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

const MessageTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Модальное окно формы
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Модальное окно просмотра
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState(null);

  // Загрузка шаблонов
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await templatesApi.getTemplates();
      setTemplates(data.items || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Открыть форму создания
  const handleCreate = () => {
    setEditingTemplate(null);
    setFormModalOpen(true);
  };

  // Открыть форму редактирования
  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormModalOpen(true);
  };

  // Открыть просмотр
  const handleView = (template) => {
    setViewingTemplate(template);
    setViewModalOpen(true);
  };

  // Удалить шаблон
  const handleDelete = async (template) => {
    const channelLabels = template.channels.map(c => CHANNEL_ICONS[c]?.label || c).join(', ');
    if (!window.confirm(`Удалить шаблон для каналов: ${channelLabels}?`)) {
      return;
    }

    try {
      await templatesApi.deleteTemplate(template.id);
      await loadTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
      alert(err.message);
    }
  };

  // После сохранения формы
  const handleFormSuccess = () => {
    setFormModalOpen(false);
    setEditingTemplate(null);
    loadTemplates();
  };

  // Редактирование из модалки просмотра
  const handleEditFromView = (template) => {
    setViewModalOpen(false);
    setViewingTemplate(null);
    setEditingTemplate(template);
    setFormModalOpen(true);
  };

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Шаблоны сообщений
            </Title>
            <Text c="dimmed" size="sm">
              Шаблоны для автозаполнения первичных сообщений
            </Text>
          </Box>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreate}
          >
            Добавить шаблон
          </Button>
        </Group>

        {/* Таблица */}
        {loading ? (
          <Center py="xl">
            <Loader size="md" />
          </Center>
        ) : templates.length === 0 ? (
          <Center py="xl">
            <Box style={{ textAlign: 'center' }}>
              <IconTemplate
                size={48}
                color={tokens.colors.neutral[4]}
                style={{ marginBottom: '16px' }}
              />
              <Text size="lg" c="dimmed" mb="xs">
                Шаблоны не найдены
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                Создайте шаблон для автозаполнения первичных сообщений
              </Text>
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={handleCreate}
              >
                Добавить шаблон
              </Button>
            </Box>
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 60 }}>ID</Table.Th>
                <Table.Th style={{ width: 200 }}>Каналы</Table.Th>
                <Table.Th>Текст сообщения</Table.Th>
                <Table.Th style={{ width: 180 }}>Создан</Table.Th>
                <Table.Th style={{ width: 60 }}>Действия</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {templates.map((template) => (
                <Table.Tr
                  key={template.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleView(template)}
                >
                  <Table.Td>
                    <Text size="sm" c="dimmed">#{template.id}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {template.channels.map((channel) => {
                        const channelInfo = CHANNEL_ICONS[channel];
                        if (!channelInfo) return null;
                        const Icon = channelInfo.icon;
                        return (
                          <Tooltip key={channel} label={channelInfo.label}>
                            <Badge
                              variant="light"
                              color={channelInfo.color}
                              leftSection={<Icon size={12} />}
                              size="sm"
                              radius="xl"
                            >
                              {channelInfo.label}
                            </Badge>
                          </Tooltip>
                        );
                      })}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={2}>
                      {template.message_text}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {formatDate(template.created_at)}
                    </Text>
                    {template.created_by && (
                      <Text size="xs" c="dimmed">
                        {template.created_by.full_name || template.created_by.email}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                        <Menu.Item
                          leftSection={<IconEye size={14} />}
                          onClick={() => handleView(template)}
                        >
                          Просмотр
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconPencil size={14} />}
                          onClick={() => handleEdit(template)}
                        >
                          Редактировать
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => handleDelete(template)}
                        >
                          Удалить
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      {/* Модальное окно формы */}
      <MessageTemplateFormModal
        opened={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingTemplate(null);
        }}
        onSuccess={handleFormSuccess}
        template={editingTemplate}
      />

      {/* Модальное окно просмотра */}
      <MessageTemplateViewModal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingTemplate(null);
        }}
        template={viewingTemplate}
        onEdit={handleEditFromView}
      />
    </Box>
  );
};

export default MessageTemplatesPage;
